import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getCourseById } from '../services/courseService';

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCourse = async () => {
      try {
        setLoading(true);
        const courseData = await getCourseById(courseId);
        
        if (!courseData) {
          setError('Course not found');
          return;
        }
        
        setCourse(courseData);
      } catch (err) {
        console.error('Error loading course:', err);
        setError('Failed to load course data');
      } finally {
        setLoading(false);
      }
    };
    
    loadCourse();
  }, [courseId]);

  // Handle loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">{error}</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          The course you're looking for could not be found or there was an error loading it.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  // If no course data is available
  if (!course) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Course Not Found</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          The course you're looking for could not be found.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  // Default learning objectives if not provided
  const learningObjectives = course.whatYouWillLearn || [
    "Understanding core concepts and terminology",
    "Applying theoretical knowledge to practical scenarios",
    "Developing problem-solving skills related to the subject",
    "Recognizing patterns and best practices in the field"
  ];

  return (
    <div>
      {/* Course Header */}
      <div className="relative h-64 sm:h-80 rounded-xl overflow-hidden mb-8">
        <img 
          src={course.image} 
          alt={course.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
          <div className="max-w-4xl">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="px-2.5 py-0.5 bg-primary-500 bg-opacity-90 text-white text-xs font-medium rounded">
                {course.category}
              </span>
              <span className="px-2.5 py-0.5 bg-gray-700 bg-opacity-90 text-white text-xs font-medium rounded">
                {course.level}
              </span>
              <span className="px-2.5 py-0.5 bg-gray-700 bg-opacity-90 text-white text-xs font-medium rounded">
                {course.duration}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-2">
              {course.title}
            </h1>
            <p className="text-gray-200 text-sm sm:text-base">
              {course.description}
            </p>
          </div>
        </div>
      </div>

      {/* Course Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-8">
            <h2 className="text-xl font-heading font-bold text-gray-900 dark:text-white mb-4">
              About This Course
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p>{course.description}</p>
              <p>This course is designed for {course.level.toLowerCase()} learners who want to understand the fundamentals of artificial intelligence and its practical applications.</p>
              <h3>What You'll Learn</h3>
              <ul>
                {learningObjectives.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-heading font-bold text-gray-900 dark:text-white mb-4">
              Course Content
            </h2>
            
            <div className="space-y-4">
              {course.lessons && course.lessons.map((lesson, index) => (
                <div 
                  key={lesson.id} 
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  <div className="bg-gray-50 dark:bg-gray-700 flex justify-between items-center p-4">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 font-medium text-sm">
                        {index + 1}
                      </span>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {lesson.title}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                      <span>{lesson.duration}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white dark:bg-gray-800">
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      {lesson.description || 'Learn the key concepts and practical applications in this lesson.'}
                    </p>
                    <Link 
                      to={`/dashboard/lessons/${lesson.id}`}
                      className="inline-flex items-center text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Start Lesson
                      <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                      </svg>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6 sticky top-6">
            <h2 className="text-xl font-heading font-bold text-gray-900 dark:text-white mb-4">
              Course Information
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Instructor</h3>
                <p className="text-gray-900 dark:text-white">{course.instructor || 'Neura Academy Team'}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Duration</h3>
                <p className="text-gray-900 dark:text-white">{course.duration}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Lessons</h3>
                <p className="text-gray-900 dark:text-white">{course.lessons?.length || 0} lessons</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Level</h3>
                <p className="text-gray-900 dark:text-white">{course.level}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Category</h3>
                <p className="text-gray-900 dark:text-white">{course.category}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Last Updated</h3>
                <p className="text-gray-900 dark:text-white">{course.lastUpdated || 'June 2023'}</p>
              </div>
            </div>
            
            <div className="mt-6">
              <Link 
                to={course.lessons?.length > 0 ? `/dashboard/lessons/${course.lessons[0].id}` : '#'}
                className={`w-full py-2.5 px-4 text-center rounded-md font-medium ${
                  course.lessons?.length > 0 
                    ? 'bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600' 
                    : 'bg-gray-300 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
                } transition-colors`}
              >
                {course.lessons?.length > 0 ? 'Start Learning' : 'No Lessons Available'}
              </Link>
            </div>
          </div>
          
          {course.features && course.features.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-heading font-bold text-gray-900 dark:text-white mb-4">
                Course Features
              </h2>
              
              <ul className="space-y-3">
                {course.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 dark:text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseDetail; 